/* eslint-disable react/destructuring-assignment */
import { createContext, ReactNode, useContext, useMemo } from "react";
import {
  Table,
  Tbody,
  Tr,
  Th,
  Td,
  TableProps,
  TableColumnHeaderProps,
  TableCellProps,
  TableRowProps,
  Skeleton,
  SkeletonProps,
} from "@chakra-ui/react";
import { GRID_PROPS } from "components/Shared/LabeledValueGrid";
import {
  DisplayedValueProps,
  IsLoadedProps,
  LabeledValueLoadingContext,
  getDisplayedValue,
  getIsLoaded,
} from "components/Shared/LabeledValue";

type LabeledValueRowContextProps = LabeledValueLoadingContext & {
  cellProps?: TableColumnHeaderProps & TableCellProps;
  labelProps?: TableColumnHeaderProps;
  rowProps?: TableRowProps;
  skeletonProps?: SkeletonProps;
  valueProps?: TableCellProps;
};

type LabeledValueTableContext = LabeledValueRowContextProps;

const labeledValueTableContext = createContext<LabeledValueTableContext>({});

type LabeledValueRowProps = LabeledValueRowContextProps &
  IsLoadedProps &
  DisplayedValueProps & {
    label: ReactNode;
  };

export function LabeledValueRow({
  cellProps,
  isLoaded: isLoadedProp,
  isLoading: isLoadingProp,
  isValueLoaded,
  isValuePresent,
  label,
  labelProps,
  rowProps,
  skeletonProps,
  value,
  valuePlaceholder,
  valueProps,
}: LabeledValueRowProps) {
  const context = useContext(labeledValueTableContext);
  const combinedLabelProps = {
    ...context.cellProps,
    ...cellProps,
    ...context.labelProps,
    ...labelProps,
  };

  const isLoaded = getIsLoaded({
    isLoaded: isLoadedProp,
    isLoadedContext: context.isLoaded,
    isLoading: isLoadingProp,
    isLoadingContext: context.isLoading,
    isValueLoaded,
    isValuePresent,
    value,
  });

  return (
    <Tr verticalAlign="baseline" {...context.rowProps} {...rowProps}>
      <Th
        {...combinedLabelProps}
        // eslint-disable-next-line no-underscore-dangle
        _last={{
          pr: combinedLabelProps?.pr || 4,
          // eslint-disable-next-line no-underscore-dangle
          ...combinedLabelProps?._last,
        }}
        scope="row"
      >
        {label}
      </Th>
      <Td
        {...context.cellProps}
        {...cellProps}
        {...context.valueProps}
        {...valueProps}
      >
        <Skeleton
          isLoaded={isLoaded}
          display="flex"
          flexFlow="column"
          {...context.skeletonProps}
          {...skeletonProps}
        >
          {getDisplayedValue({
            isValuePresent,
            value,
            valuePlaceholder,
          })}
        </Skeleton>
      </Td>
    </Tr>
  );
}

export type LabeledValueTableProps = TableProps & LabeledValueTableContext;

export default function LabeledValueTable({
  cellProps,
  children,
  isLoaded,
  isLoading,
  labelProps,
  rowProps,
  skeletonProps,
  valueProps,
  ...rest
}: LabeledValueTableProps) {
  const context = useMemo(
    () => ({
      cellProps,
      isLoaded,
      isLoading,
      labelProps,
      rowProps,
      skeletonProps,
      valueProps,
    }),
    [
      cellProps,
      isLoaded,
      isLoading,
      labelProps,
      rowProps,
      skeletonProps,
      valueProps,
    ],
  );
  return (
    <Table variant="labeledValue" {...rest}>
      <Tbody sx={{ ">tr": { borderBottomWidth: 0 } }}>
        <labeledValueTableContext.Provider value={context}>
          {children}
        </labeledValueTableContext.Provider>
      </Tbody>
    </Table>
  );
}

export const GRID_LAYOUT_PROPS: Partial<LabeledValueTableProps> = {
  display: "block",
  rowProps: { display: "block" },
  cellProps: { display: "block", p: 0, lineHeight: "base" },
  labelProps: { textAlign: "left" },
  sx: {
    ">tbody": { display: "grid", ...GRID_PROPS },
  },
};
